<template>
  <div ref="switcherEl" class="language-switcher">
    <button
      type="button"
      class="language-switch-button"
      :class="`nlc-${nlcIndicator}`"
      :data-nlc-state="nlcIndicator"
      :title="switcherLabel"
      :aria-label="switcherLabel"
      :aria-expanded="menuOpen ? 'true' : 'false'"
      @click="toggleMenu"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M3.5 12h17"></path>
        <path d="M12 3c2.2 2.5 3.4 5.5 3.4 9s-1.2 6.5-3.4 9"></path>
        <path d="M12 3c-2.2 2.5-3.4 5.5-3.4 9s1.2 6.5 3.4 9"></path>
      </svg>
    </button>

    <section v-if="menuOpen" class="language-menu" :aria-label="language.t('common.language')">
      <button
        v-for="item in languageOptions"
        :key="item.key"
        type="button"
        class="language-option"
        :class="{ active: item.key === language.current }"
        @click="selectLanguage(item.key)"
      >
        <span class="language-check" aria-hidden="true">{{ item.key === language.current ? '✓' : '' }}</span>
        <img class="language-flag" :src="item.flagSrc" :alt="item.flagLabel" loading="lazy" decoding="async">
        <span class="language-name">{{ item.menuLabel || item.native }}</span>
      </button>
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { languageOptions, useLanguageStore } from '@/stores/language';
import { useSessionStore } from '@/stores/session';
import { getNlcStatus, nlcIndicatorState } from '@/services/messageTranslation';

const language = useLanguageStore();
const session = useSessionStore();
const menuOpen = ref(false);
const switcherEl = ref(null);
const nlcIndicator = ref('off');
let nlcRefreshTimer = 0;
let nlcRequestSerial = 0;
const indicatorLabelKeys = {
  off: 'settings.nlcIndicatorOff',
  active: 'settings.nlcIndicatorActive',
  warning: 'settings.nlcIndicatorWarning',
  exhausted: 'settings.nlcIndicatorExhausted'
};
const switcherLabel = computed(() => `${language.t('common.switchLanguage')} · ${language.t(indicatorLabelKeys[nlcIndicator.value])}`);

function applyNlcIndicator(payload) {
  nlcIndicator.value = nlcIndicatorState(payload);
}

async function refreshNlcIndicator() {
  if (!session.token) {
    nlcIndicator.value = 'off';
    return;
  }
  const serial = ++nlcRequestSerial;
  try {
    const payload = await getNlcStatus({ token: session.token });
    if (serial === nlcRequestSerial) {
      applyNlcIndicator(payload);
    }
  } catch {
    // Keep the last known state when a background refresh fails.
  }
}

function handleNlcPreferenceChanged(event) {
  if (event.detail?.payload) {
    applyNlcIndicator(event.detail.payload);
    return;
  }
  if (event.detail?.enabled === false) {
    nlcIndicator.value = 'off';
  }
  refreshNlcIndicator();
}

function refreshNlcIndicatorWhenVisible() {
  if (document.visibilityState === 'visible') {
    refreshNlcIndicator();
  }
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function selectLanguage(key) {
  language.setLanguage(key);
  closeMenu();
}

function handlePointerDown(event) {
  if (!menuOpen.value || switcherEl.value?.contains(event.target)) {
    return;
  }
  closeMenu();
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    closeMenu();
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', handlePointerDown);
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('visibilitychange', refreshNlcIndicatorWhenVisible);
  window.addEventListener('focus', refreshNlcIndicator);
  window.addEventListener('jc-nlc-preference-changed', handleNlcPreferenceChanged);
  refreshNlcIndicator();
  nlcRefreshTimer = window.setInterval(refreshNlcIndicator, 60000);
});

onBeforeUnmount(() => {
  nlcRequestSerial += 1;
  window.clearInterval(nlcRefreshTimer);
  document.removeEventListener('pointerdown', handlePointerDown);
  document.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('visibilitychange', refreshNlcIndicatorWhenVisible);
  window.removeEventListener('focus', refreshNlcIndicator);
  window.removeEventListener('jc-nlc-preference-changed', handleNlcPreferenceChanged);
});
</script>
